import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { getAnnualEntitlement, getCarryStartYear } from '../../utils/leaveBalance';
import { leaveTimeline, summarizeLeaves, toLeaveRecord } from './leaveSummary';

const BATCH_SIZE = 30; // Firestore 'in' sınırı

const fetchByEmployees = async (collectionName, ids) => {
  const batches = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) batches.push(ids.slice(i, i + BATCH_SIZE));
  const snapshots = await Promise.all(
    batches.map((batch) => getDocs(query(collection(db, collectionName), where('employeeId', 'in', batch))))
  );
  return snapshots.flatMap((snap) => snap.docs.map((d) => toLeaveRecord({ id: d.id, ...d.data() }, collectionName)));
};

/**
 * Ekipteki çalışanların tüm izin kayıtları (çalışan talepleri + İzin
 * Yönetimi'nden eklenenler). Liste her yenilendiğinde bir kez çekilir; tablodaki
 * İzin sütunu (bu yılın kalanı, şu an izinde mi) ve izin detay penceresi bunu kullanır.
 */
const useTeamLeaves = (workers) => {
  const [recordsByWorker, setRecordsByWorker] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const ids = [...new Set(workers.map((w) => w.id).filter(Boolean))];
    if (ids.length === 0) {
      setRecordsByWorker({});
      return undefined;
    }

    let cancelled = false;
    const fetchLeaves = async () => {
      setLoading(true);
      setError(false);
      try {
        const [requests, leaves] = await Promise.all([
          fetchByEmployees('LeaveRequests', ids),
          fetchByEmployees('EmployeeLeaves', ids),
        ]);
        const grouped = {};
        [...requests, ...leaves].forEach((record) => {
          if (!grouped[record.employeeId]) grouped[record.employeeId] = [];
          grouped[record.employeeId].push(record);
        });
        if (!cancelled) setRecordsByWorker(grouped);
      } catch (err) {
        // İzin bilgisi ikincil: hata durumunda tablo izinsiz çalışmaya devam eder
        console.error('Ekip izin kayıtları alınamadı:', err);
        if (!cancelled) {
          setRecordsByWorker({});
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLeaves();
    return () => {
      cancelled = true;
    };
  }, [workers]);

  // Tablo için çalışan başına kısa özet: bu yılın kalan günü, şu an izinde mi
  const summaryByWorker = useMemo(() => {
    const year = new Date().getFullYear();
    const result = {};
    workers.forEach((worker) => {
      const records = recordsByWorker[worker.id] || [];
      const { balance, pendingCount } = summarizeLeaves({ records, entitlement: getAnnualEntitlement(worker), carryFromYear: getCarryStartYear(worker), year });
      result[worker.id] = { remaining: balance.remaining, pendingCount, current: leaveTimeline(records).current };
    });
    return result;
  }, [workers, recordsByWorker]);

  return { recordsByWorker, summaryByWorker, loading, error };
};

export default useTeamLeaves;
